import { useCallback } from 'react';
import { useAuth } from '@/contexts/AuthContext';
import { useBillingStatus } from '@/hooks/useBillingStatus';
import { useTrialCountdown } from '@/hooks/useTrialCountdown';
import { useUpgradeWallStore } from '@/stores/upgradeWallStore';
import { isTrialExpiredForCompany, resolveTrialPlanContext } from '@/lib/trialGating';

/**
 * True when freemium trial ended and the selected company is not on a paid plan.
 * Waits for company billing to load before blocking — avoids a flash for users
 * who belong to paid/unlimited companies but still have user-level freemium_trial.
 */
export function useTrialWriteBlocked(): boolean {
  const { selectedCompany, user } = useAuth();
  const { data: trial } = useTrialCountdown();
  const { data: billingStatus } = useBillingStatus();

  const plan = resolveTrialPlanContext(user, selectedCompany, billingStatus);

  if (plan.isAgencyOwnerEffective || plan.onPaidPlan) return false;

  // User-level freemium_trial is not authoritative for multi-company accounts.
  if (selectedCompany?.id && !plan.billingResolved) {
    return false;
  }

  return isTrialExpiredForCompany(trial);
}

/** Blocks write actions when trial expired — opens upgrade wall and returns false. */
export function useTrialWriteGuard() {
  const writeBlocked = useTrialWriteBlocked();
  const { data: trial } = useTrialCountdown();
  const show = useUpgradeWallStore((s) => s.show);

  const guardWrite = useCallback((): boolean => {
    if (!writeBlocked) return true;
    show({
      reason: 'trial_expired',
      trial_ended_at: trial?.active ? trial.endsAt : undefined,
    });
    return false;
  }, [writeBlocked, show, trial]);

  return { writeBlocked, guardWrite };
}
